import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { formatDateBR } from "../../utils/format";

type ConsentStatus = "ACTIVE" | "REVOKED" | "PENDING";

type Consent = {
  id: number;
  provider: "MERCADO_PAGO" | "BTG";
  externalId: string;
  status: ConsentStatus;
  tokenExpiresAt: string | null;
  expiresAt: string | null;
  lastSyncAt: string | null;
  createdAt: string;
};

const PROVIDER_META: Record<
  string,
  { label: string; color: string; logo: string; description: string }
> = {
  MERCADO_PAGO: {
    label: "Mercado Pago",
    color: "#00b1ea",
    logo: "💙",
    description: "Conta digital e carteira Mercado Pago",
  },
  BTG: {
    label: "BTG Pactual",
    color: "#e8a000",
    logo: "🏦",
    description: "Banco BTG Pactual",
  },
};

const statusBadge = (status: ConsentStatus) => {
  if (status === "ACTIVE") return <span className="badge text-bg-success">Ativo</span>;
  if (status === "REVOKED") return <span className="badge text-bg-danger">Revogado</span>;
  return <span className="badge text-bg-warning text-dark">Pendente</span>;
};

const isExpiringSoon = (expiresAt: string | null): boolean => {
  if (!expiresAt) return false;
  const diff = new Date(expiresAt).getTime() - Date.now();
  return diff > 0 && diff < 7 * 24 * 60 * 60 * 1000; // less than 7 days
};

const isExpired = (expiresAt: string | null): boolean => {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
};

export const OpenFinanceConnectionsPage: React.FC = () => {
  const { client } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [consents, setConsents] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectingMp, setConnectingMp] = useState(false);
  const [revoking, setRevoking] = useState<number | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Manual BTG consent form
  const [btgFormOpen, setBtgFormOpen] = useState(false);
  const [btgExternalId, setBtgExternalId] = useState("");
  const [btgAccessToken, setBtgAccessToken] = useState("");
  const [btgRefreshToken, setBtgRefreshToken] = useState("");
  const [btgExpiresAt, setBtgExpiresAt] = useState("");
  const [btgSaving, setBtgSaving] = useState(false);
  const [btgError, setBtgError] = useState<string | null>(null);

  // Handle OAuth redirect result
  useEffect(() => {
    const connected = searchParams.get("connected");
    const oauthError = searchParams.get("error");

    if (connected === "mercado-pago") {
      setSuccessMsg("Mercado Pago conectado com sucesso!");
      setSearchParams({}, { replace: true });
    } else if (oauthError) {
      setError(decodeURIComponent(oauthError));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadConsents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/integrations/openfinance/consents?take=50&skip=0");
      if (!res.ok) throw new Error("Erro ao carregar conexões");
      const payload = (await res.json()) as { data: Consent[] };
      setConsents(payload.data);
    } catch {
      setError("Erro ao carregar conexões.");
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    void loadConsents();
  }, [loadConsents]);

  const handleConnectMp = async () => {
    setConnectingMp(true);
    setError(null);
    try {
      const res = await client.get("/integrations/openfinance/connect-url/mercado-pago");
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(payload.error ?? "Erro ao obter URL de autorização.");
      }
      const payload = (await res.json()) as { url: string };
      // Redirect the current tab to MP authorization page
      window.location.href = payload.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao conectar.");
      setConnectingMp(false);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!window.confirm("Revogar esta conexão? O sistema deixará de sincronizar dados deste provedor.")) return;
    setRevoking(id);
    try {
      await client.post(`/integrations/openfinance/consents/${id}/revoke`, {});
      await loadConsents();
    } finally {
      setRevoking(null);
    }
  };

  const handleSaveBtg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!btgExternalId || !btgAccessToken) {
      setBtgError("ID Externo e Token de Acesso são obrigatórios.");
      return;
    }
    setBtgSaving(true);
    setBtgError(null);

    // Fetch entity ID from current user context
    const entityRes = await client.get("/modules/bank-accounts?take=1&skip=0");
    if (!entityRes.ok) {
      setBtgError("Não foi possível identificar a entidade.");
      setBtgSaving(false);
      return;
    }

    // Get entityId from any existing resource
    const entityPayload = (await entityRes.json()) as {
      data: Array<{ entityId?: number }>;
    };
    const entityId = entityPayload.data[0]?.entityId;
    if (!entityId) {
      setBtgError("Entidade não encontrada. Configure uma conta bancária primeiro.");
      setBtgSaving(false);
      return;
    }

    try {
      const res = await client.post("/integrations/openfinance/consents", {
        entityId,
        provider: "BTG",
        externalId: btgExternalId,
        accessToken: btgAccessToken,
        refreshToken: btgRefreshToken || undefined,
        tokenExpiresAt: btgExpiresAt || undefined,
      });
      if (!res.ok) {
        const p = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(p.error ?? "Erro ao salvar.");
      }
      setBtgFormOpen(false);
      setBtgExternalId("");
      setBtgAccessToken("");
      setBtgRefreshToken("");
      setBtgExpiresAt("");
      setSuccessMsg("Conexão BTG salva com sucesso!");
      await loadConsents();
    } catch (err) {
      setBtgError(err instanceof Error ? err.message : "Erro ao salvar.");
    } finally {
      setBtgSaving(false);
    }
  };

  const consentsByProvider = {
    MERCADO_PAGO: consents.filter((c) => c.provider === "MERCADO_PAGO"),
    BTG: consents.filter((c) => c.provider === "BTG"),
  };

  return (
    <div className="container py-4" style={{ maxWidth: 900 }}>
      <h1 className="mb-1">Conexões Open Finance</h1>
      <p className="text-muted mb-4">
        Conecte suas contas bancárias para sincronização automática de transações.
      </p>

      {successMsg && (
        <div className="alert alert-success d-flex justify-content-between align-items-center">
          <span>{successMsg}</span>
          <button className="btn-close" onClick={() => setSuccessMsg(null)} />
        </div>
      )}
      {error && (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError(null)} />
        </div>
      )}

      {/* ── Mercado Pago ── */}
      <div className="card shadow-sm mb-4">
        <div className="card-header d-flex align-items-center gap-2">
          <span style={{ fontSize: "1.4rem" }}>{PROVIDER_META.MERCADO_PAGO.logo}</span>
          <div>
            <div className="fw-semibold">{PROVIDER_META.MERCADO_PAGO.label}</div>
            <div className="text-muted small">{PROVIDER_META.MERCADO_PAGO.description}</div>
          </div>
          <div className="ms-auto">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleConnectMp}
              disabled={connectingMp}
            >
              {connectingMp ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  Redirecionando...
                </>
              ) : (
                "+ Adicionar conexão"
              )}
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="p-3 text-muted small">Carregando...</div>
          ) : consentsByProvider.MERCADO_PAGO.length === 0 ? (
            <div className="p-3 text-muted small">
              Nenhuma conexão ativa. Clique em "Adicionar conexão" para autorizar o acesso à sua conta Mercado Pago.
            </div>
          ) : (
            <table className="table table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID Externo</th>
                  <th>Status</th>
                  <th>Token expira em</th>
                  <th>Última sincronia</th>
                  <th>Criado em</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {consentsByProvider.MERCADO_PAGO.map((c) => (
                  <tr key={c.id}>
                    <td className="text-monospace small">{c.externalId}</td>
                    <td>{statusBadge(c.status)}</td>
                    <td>
                      {c.tokenExpiresAt ? (
                        <span
                          className={
                            isExpired(c.tokenExpiresAt)
                              ? "text-danger"
                              : isExpiringSoon(c.tokenExpiresAt)
                              ? "text-warning"
                              : ""
                          }
                        >
                          {formatDateBR(c.tokenExpiresAt)}
                          {isExpired(c.tokenExpiresAt) && " (expirado)"}
                          {isExpiringSoon(c.tokenExpiresAt) && " (expira em breve)"}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {c.lastSyncAt ? (
                        formatDateBR(c.lastSyncAt)
                      ) : (
                        <span className="text-muted">Nunca</span>
                      )}
                    </td>
                    <td>{formatDateBR(c.createdAt)}</td>
                    <td>
                      {c.status === "ACTIVE" && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => void handleRevoke(c.id)}
                          disabled={revoking === c.id}
                        >
                          {revoking === c.id ? "..." : "Revogar"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card-footer text-muted small">
          <strong>Como configurar:</strong> Crie um app em{" "}
          <a href="https://developers.mercadopago.com.br" target="_blank" rel="noopener noreferrer">
            developers.mercadopago.com.br
          </a>{" "}
          e defina as variáveis de ambiente{" "}
          <code>OPENFINANCE_MP_CLIENT_ID</code>, <code>OPENFINANCE_MP_CLIENT_SECRET</code> e{" "}
          <code>OPENFINANCE_MP_REDIRECT_URI</code> no servidor da API.
          O Redirect URI deve ser:{" "}
          <code>{`${window.location.origin.replace("5173", "3000")}/integrations/openfinance/callback/mercado-pago`}</code>
          {" "}(URL da API, não do admin).
        </div>
      </div>

      {/* ── BTG Pactual ── */}
      <div className="card shadow-sm mb-4">
        <div className="card-header d-flex align-items-center gap-2">
          <span style={{ fontSize: "1.4rem" }}>{PROVIDER_META.BTG.logo}</span>
          <div>
            <div className="fw-semibold">{PROVIDER_META.BTG.label}</div>
            <div className="text-muted small">{PROVIDER_META.BTG.description}</div>
          </div>
          <div className="ms-auto">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => { setBtgFormOpen((v) => !v); setBtgError(null); }}
            >
              {btgFormOpen ? "Fechar" : "+ Configurar manualmente"}
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          {consentsByProvider.BTG.length > 0 && (
            <table className="table table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th>ID Externo</th>
                  <th>Status</th>
                  <th>Token expira em</th>
                  <th>Última sincronia</th>
                  <th>Criado em</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {consentsByProvider.BTG.map((c) => (
                  <tr key={c.id}>
                    <td className="text-monospace small">{c.externalId}</td>
                    <td>{statusBadge(c.status)}</td>
                    <td>
                      {c.tokenExpiresAt ? (
                        <span className={isExpired(c.tokenExpiresAt) ? "text-danger" : ""}>
                          {formatDateBR(c.tokenExpiresAt)}
                          {isExpired(c.tokenExpiresAt) && " (expirado)"}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{c.lastSyncAt ? formatDateBR(c.lastSyncAt) : <span className="text-muted">Nunca</span>}</td>
                    <td>{formatDateBR(c.createdAt)}</td>
                    <td>
                      {c.status === "ACTIVE" && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => void handleRevoke(c.id)}
                          disabled={revoking === c.id}
                        >
                          {revoking === c.id ? "..." : "Revogar"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {btgFormOpen && (
            <div className="p-3 border-top">
              <p className="text-muted small mb-3">
                O BTG Pactual participa do <strong>Open Finance Brasil</strong> (regulado pelo Banco Central).
                Para integração direta, é necessário registro institucional como Receptor de Dados.
                Alternativamente, use um agregador como{" "}
                <a href="https://pluggy.ai" target="_blank" rel="noopener noreferrer">Pluggy</a>{" "}
                ou{" "}
                <a href="https://belvo.com" target="_blank" rel="noopener noreferrer">Belvo</a>{" "}
                e informe o token gerado por eles abaixo.
              </p>
              <form onSubmit={(e) => void handleSaveBtg(e)}>
                {btgError && <div className="alert alert-danger py-2 small">{btgError}</div>}
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">
                      ID Externo <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Ex: user_id do agregador"
                      value={btgExternalId}
                      onChange={(e) => setBtgExternalId(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">
                      Base URL da API
                    </label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Definida via OPENFINANCE_BTG_BASE_URL no servidor"
                      disabled
                    />
                    <div className="form-text">Configure no .env do servidor</div>
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold">
                      Access Token <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className="form-control form-control-sm font-monospace"
                      placeholder="Token de acesso"
                      value={btgAccessToken}
                      onChange={(e) => setBtgAccessToken(e.target.value)}
                    />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small fw-semibold">Refresh Token</label>
                    <input
                      type="password"
                      className="form-control form-control-sm font-monospace"
                      placeholder="Opcional"
                      value={btgRefreshToken}
                      onChange={(e) => setBtgRefreshToken(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Token expira em</label>
                    <input
                      type="datetime-local"
                      className="form-control form-control-sm"
                      value={btgExpiresAt}
                      onChange={(e) => setBtgExpiresAt(e.target.value)}
                    />
                  </div>
                  <div className="col-12 d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm"
                      disabled={btgSaving}
                    >
                      {btgSaving ? "Salvando..." : "Salvar conexão BTG"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setBtgFormOpen(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {!btgFormOpen && consentsByProvider.BTG.length === 0 && (
            <div className="p-3 text-muted small">
              Nenhuma conexão configurada. Clique em "Configurar manualmente" para adicionar um token via agregador.
            </div>
          )}
        </div>

        <div className="card-footer text-muted small">
          <strong>Opções de integração BTG:</strong>{" "}
          (1) <a href="https://pluggy.ai" target="_blank" rel="noopener noreferrer">Pluggy</a> —
          agregador brasileiro com suporte a 300+ bancos, incluindo BTG. Crie uma conta, conecte o BTG
          no dashboard deles e use o access token gerado aqui. Configure{" "}
          <code>OPENFINANCE_BTG_BASE_URL</code> com a URL da API do Pluggy.{" "}
          (2) Registro direto no Open Finance Brasil (requer certificados e aprovação do Banco Central).
        </div>
      </div>

      {/* ── Genial ── */}
      <div className="card shadow-sm mb-4">
        <div className="card-header d-flex align-items-center gap-2">
          <span style={{ fontSize: "1.4rem" }}>📊</span>
          <div>
            <div className="fw-semibold">Genial Investimentos</div>
            <div className="text-muted small">Corretora de valores — extrato via PDF</div>
          </div>
          <div className="ms-auto">
            <a href="/financeiro/importar-ofx" className="btn btn-outline-secondary btn-sm">
              Importar extrato PDF
            </a>
          </div>
        </div>
        <div className="card-body">
          <div className="d-flex align-items-start gap-3">
            <span style={{ fontSize: "2rem" }}>ℹ️</span>
            <div>
              <p className="mb-1">
                A Genial Investimentos é uma <strong>corretora de valores</strong>, não um banco, e não
                participa do Open Finance Brasil (que é exclusivo para instituições bancárias reguladas).
                Por isso, <strong>não é possível criar uma conexão automática</strong> via OAuth.
              </p>
              <p className="mb-0 text-muted small">
                Para importar o extrato da Genial, baixe o PDF na área do cliente em{" "}
                <a href="https://www.genialinvestimentos.com.br" target="_blank" rel="noopener noreferrer">
                  genialinvestimentos.com.br
                </a>{" "}
                → Conta Corrente → Extrato, e use a opção{" "}
                <strong>"Importar OFX"</strong> selecionando o tipo <strong>"PDF Genial"</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
